import React, { Children, Fragment, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@heroui/react';
import ChevronDown from '@/components/ChevronDown';

type KeyLike = string | number;
type KeySet = Set<KeyLike> & { currentKey?: string; anchorKey?: string };

export interface SelectItemProps {
    children?: ReactNode;
    textValue?: string;
    isDisabled?: boolean;
    description?: ReactNode;
    className?: string;
    classNames?: Record<string, string>;
    [key: string]: any;
}

export function SelectItem(_props: SelectItemProps): React.ReactElement | null {
    return null;
}

interface SelectClassNames {
    base?: string;
    label?: string;
    trigger?: string;
    value?: string;
    selectorIcon?: string;
    errorMessage?: string;
    [key: string]: string | undefined;
}

export interface SelectProps {
    children?: ReactNode | ((item: any) => React.ReactElement);
    items?: Iterable<any>;
    label?: ReactNode;
    placeholder?: string;
    className?: string;
    classNames?: SelectClassNames;
    selectedKeys?: Iterable<KeyLike> | 'all';
    defaultSelectedKeys?: Iterable<KeyLike>;
    onSelectionChange?: (keys: KeySet) => void;
    onChange?: (event: { target: { value: string; name?: string } }) => void;
    disallowEmptySelection?: boolean;
    isDisabled?: boolean;
    isRequired?: boolean;
    isInvalid?: boolean;
    errorMessage?: ReactNode;
    startContent?: ReactNode;
    selectorIcon?: ReactNode;
    name?: string;
    id?: string;
    'aria-label'?: string;
    [key: string]: any;
}

interface Option {
    key: string;
    label: string;
    description?: ReactNode;
    disabled: boolean;
}

const textOf = (node: ReactNode): string => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(textOf).join('');
    if (isValidElement(node)) return textOf((node.props as any).children);
    return '';
};

const collect = (nodes: ReactNode, out: Option[]) => {
    Children.forEach(nodes, child => {
        if (!isValidElement(child)) return;
        if (child.type === Fragment) {
            collect((child.props as any).children, out);
            return;
        }
        const props = child.props as SelectItemProps;
        const key = child.key !== null ? String(child.key).replace(/^\.\$/, '') : '';
        out.push({ key, label: props.textValue ?? (textOf(props.children) || key), description: props.description, disabled: !!props.isDisabled });
    });
};

const firstKey = (keys: Iterable<KeyLike> | 'all' | undefined): string | undefined => {
    if (keys === undefined || keys === 'all') return undefined;
    for (const k of keys) return String(k);
    return undefined;
};

const SHEET_BREAKPOINT = 640;
const LIST_MAX_HEIGHT = 280;
const TOP_LAYER = { 'data-react-aria-top-layer': 'true' } as const;

interface Placement {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
}

const placementFor = (rect: DOMRect): Placement => {
    const gap = 6;
    const below = window.innerHeight - rect.bottom - gap - 8;
    const above = rect.top - gap - 8;
    if (below >= Math.min(LIST_MAX_HEIGHT, 160) || below >= above) {
        return { top: rect.bottom + gap, left: rect.left, width: rect.width, maxHeight: Math.min(LIST_MAX_HEIGHT, Math.max(120, below)) };
    }
    return { bottom: window.innerHeight - rect.top + gap, left: rect.left, width: rect.width, maxHeight: Math.min(LIST_MAX_HEIGHT, Math.max(120, above)) };
};

export function Select({
    children,
    items,
    label,
    placeholder,
    className,
    classNames,
    selectedKeys,
    defaultSelectedKeys,
    onSelectionChange,
    onChange,
    isDisabled,
    isRequired,
    isInvalid,
    errorMessage,
    startContent,
    selectorIcon,
    name,
    id,
    'aria-label': ariaLabel,
}: SelectProps) {
    const options = useMemo(() => {
        const out: Option[] = [];
        if (typeof children === 'function' && items) {
            for (const item of items) collect(children(item), out);
        } else {
            collect(children as ReactNode, out);
        }
        return out;
    }, [children, items]);

    const controlled = selectedKeys !== undefined;
    const [inner, setInner] = useState(firstKey(defaultSelectedKeys) ?? '');
    const raw = controlled ? firstKey(selectedKeys) ?? '' : inner;
    const current = options.find(o => o.key === raw);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [open, setOpen] = useState(false);
    const [sheet, setSheet] = useState(false);
    const [placement, setPlacement] = useState<Placement | null>(null);
    const [active, setActive] = useState(-1);

    const close = useCallback(() => setOpen(false), []);

    const openList = () => {
        if (isDisabled || !triggerRef.current) return;
        const isSheet = window.innerWidth < SHEET_BREAKPOINT;
        setSheet(isSheet);
        setPlacement(isSheet ? null : placementFor(triggerRef.current.getBoundingClientRect()));
        setActive(Math.max(0, options.findIndex(o => o.key === raw)));
        setOpen(true);
    };

    const choose = (option: Option) => {
        if (option.disabled) return;
        if (!controlled) setInner(option.key);
        const keys: KeySet = new Set<KeyLike>([option.key]);
        keys.currentKey = option.key;
        keys.anchorKey = option.key;
        onSelectionChange?.(keys);
        onChange?.({ target: { value: option.key, name } });
        close();
        triggerRef.current?.focus({ preventScroll: true });
    };

    useLayoutEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
        el?.scrollIntoView({ block: 'nearest' });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = (e: Event) => {
            if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
            close();
        };
        const onResize = () => close();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, close]);

    useEffect(() => {
        if (isDisabled) setOpen(false);
    }, [isDisabled]);

    const move = (delta: number) => {
        if (options.length === 0) return;
        let next = active;
        for (let n = 0; n < options.length; n++) {
            next = (next + delta + options.length) % options.length;
            if (!options[next].disabled) break;
        }
        setActive(next);
        listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[next]?.scrollIntoView({ block: 'nearest' });
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (isDisabled) return;
        if (!open) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                openList();
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            move(1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            move(-1);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (options[active]) choose(options[active]);
        } else if (e.key === 'Tab') {
            close();
        }
    };

    const optionRows = options.map((o, i) => {
        const selected = o.key === raw;
        return (
            <li
                key={o.key}
                role="option"
                aria-selected={selected}
                aria-disabled={o.disabled || undefined}
                onPointerEnter={() => setActive(i)}
                onClick={() => choose(o)}
                className={cn(
                    'flex min-h-[44px] cursor-pointer items-center justify-between gap-3 px-4 py-2 text-[15px] sm:min-h-[40px]',
                    'text-grey10 dark:text-nightTextSecondary',
                    i === active && 'bg-primary99 dark:bg-white/10',
                    selected && 'font-semibold text-primary dark:text-nightAccent',
                    o.disabled && 'cursor-not-allowed opacity-40',
                )}
            >
                <span className="min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {o.description && <span className="block truncate text-xs font-normal text-grey40 dark:text-nightMuted">{o.description}</span>}
                </span>
                {selected && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
                        <path d="M3 8.5l3.2 3L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </li>
        );
    });

    const list = (
        <ul
            ref={listRef}
            role="listbox"
            aria-label={ariaLabel}
            className="m-0 list-none overflow-y-auto overscroll-contain p-0 py-1"
            style={{ maxHeight: sheet ? '55vh' : placement?.maxHeight, WebkitOverflowScrolling: 'touch' }}
        >
            {optionRows}
        </ul>
    );

    const popup = open
        ? createPortal(
              <div {...TOP_LAYER} className="fixed inset-0 z-[100100]" onPointerDown={e => e.preventDefault()}>
                  <div
                      {...TOP_LAYER}
                      className={cn('absolute inset-0', sheet && 'bg-black/40')}
                      onPointerDown={e => {
                          e.preventDefault();
                          close();
                      }}
                  />
                  {sheet ? (
                      <div
                          {...TOP_LAYER}
                          className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl dark:bg-nightElevated"
                      >
                          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-grey90 dark:bg-white/20" />
                          {label && <div className="px-4 pb-1 pt-3 text-sm font-semibold text-grey20 dark:text-nightTextSecondary">{label}</div>}
                          {list}
                      </div>
                  ) : (
                      placement && (
                          <div
                              {...TOP_LAYER}
                              className="absolute overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_-12px_rgba(0,0,0,0.35)] ring-1 ring-black/[0.06] dark:bg-nightElevated dark:ring-white/10"
                              style={{ top: placement.top, bottom: placement.bottom, left: placement.left, width: placement.width }}
                          >
                              {list}
                          </div>
                      )
                  )}
              </div>,
              document.body,
          )
        : null;

    return (
        <div className={cn('flex w-full flex-col', className, classNames?.base)}>
            {label && (
                <label htmlFor={id} className={cn('mb-1.5 text-sm font-medium text-grey20 dark:text-nightTextSecondary', classNames?.label)}>
                    {label}
                    {isRequired && <span className="ml-0.5 text-danger">*</span>}
                </label>
            )}
            <button
                ref={triggerRef}
                id={id}
                type="button"
                role="combobox"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-invalid={isInvalid || undefined}
                aria-required={isRequired || undefined}
                disabled={isDisabled}
                onClick={() => (open ? close() : openList())}
                onKeyDown={onKeyDown}
                className={cn(
                    'relative flex h-14 w-full items-center gap-2 rounded-2xl bg-grey99 px-4 text-left text-base outline-none ring-1 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10',
                    'focus-visible:ring-2 focus-visible:ring-primary/50 dark:focus-visible:ring-nightAccent/60',
                    isInvalid && 'ring-1 ring-danger',
                    isDisabled && 'cursor-not-allowed opacity-50',
                    classNames?.trigger,
                )}
            >
                {startContent}
                <span className={cn('min-w-0 flex-1 truncate', current ? 'text-grey10 dark:text-white' : 'text-grey40 dark:text-nightMuted', classNames?.value)}>
                    {current ? current.label : placeholder ?? ''}
                </span>
                <span className={cn('pointer-events-none flex shrink-0 items-center transition-none', open && 'rotate-180', classNames?.selectorIcon)}>
                    {selectorIcon ?? <ChevronDown />}
                </span>
            </button>
            {name && <input type="hidden" name={name} value={current?.key ?? ''} />}
            {isInvalid && errorMessage && <p className={cn('mt-1 text-xs text-danger', classNames?.errorMessage)}>{errorMessage}</p>}
            {popup}
        </div>
    );
}
